/**
 * Alerts: the notification list, push registration, and ding preferences.
 *
 * Push degrades in stages rather than failing. If the browser has no push
 * support, if the VAPID key endpoint is not there, or if permission is denied,
 * the screen says which one it is and stays usable. A dead "Enable
 * notifications" button that silently does nothing is the outcome being avoided.
 *
 * The boss routine is shown read-only above a person's own additions, because
 * the rule is additive: staff can add dings for themselves, never remove the
 * ones the business relies on.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, BellOff, BellRing, Check, Plus, Send, Trash2 } from 'lucide-react';
import { api, ApiError, qk, type DingRoutineEntry } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { fmtDateTime12 } from '../../lib/time.ts';
import { EmptyState, ErrorState, LoadingBlock, Spinner } from '../../components/Bits.tsx';
import { OutcomeModal } from '../../components/Overlay.tsx';

const EVENT_LABEL: Record<string, string> = {
  roster_published: 'The roster is published',
  shift_reminder: 'Before a shift starts',
  shift_changed: 'One of my shifts changes',
  payslip_ready: 'A payslip is ready',
};

function describe(d: DingRoutineEntry): string {
  const label = EVENT_LABEL[d.event] ?? d.event.replace(/_/g, ' ');
  if (d.offsetHours === 0) return `${label}, straight away`;
  if (d.offsetHours < 0) return `${label}, ${Math.abs(d.offsetHours)} hours before`;
  return `${label}, ${d.offsetHours} hours after`;
}

/** base64url to the Uint8Array the Push API wants for applicationServerKey. */
function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(padded);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

type PushState = 'unsupported' | 'install-first' | 'unavailable' | 'denied' | 'off' | 'on' | 'working';

const isIosDevice = () =>
  typeof navigator !== 'undefined' && /iphone|ipad|ipod/i.test(navigator.userAgent);
const isInstalled = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)').matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true);

function PushCard({ slug }: { slug: string }) {
  const [state, setState] = useState<PushState>('off');
  const [failure, setFailure] = useState<string | null>(null);
  const [tested, setTested] = useState<{ ok: boolean; message: string } | null>(null);
  const test = useMutation({
    mutationFn: () => api.pushTest(slug),
    onSuccess: (r) => setTested({ ok: r.ok, message: r.message }),
    onError: () =>
      setTested({ ok: false, message: 'Could not send the test. Try again in a moment.' }),
  });

  useEffect(() => {
    let cancelled = false;
    async function check() {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        // On an iPhone this is not a dead end: Apple allows web push only for
        // an app installed to the home screen, so the honest instruction is
        // "install it first" rather than "your browser cannot do this".
        if (!cancelled) setState(isIosDevice() && !isInstalled() ? 'install-first' : 'unsupported');
        return;
      }
      if (Notification.permission === 'denied') {
        if (!cancelled) setState('denied');
        return;
      }
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (!cancelled) setState(sub ? 'on' : 'off');
      } catch {
        if (!cancelled) setState('off');
      }
    }
    void check();
    return () => {
      cancelled = true;
    };
  }, []);

  async function enable() {
    setState('working');
    try {
      // The key endpoint is optional in the v1 contract. Its absence means push
      // is not configured on this server, which is a different sentence from
      // "something went wrong".
      let publicKey: string;
      try {
        const { key } = await api.vapidKey();
        // A null key means push is not configured on this server. That is a
        // different sentence from "something went wrong", and it must not
        // reach pushManager.subscribe as undefined.
        if (!key) {
          setState('unavailable');
          return;
        }
        publicKey = key;
      } catch (err) {
        if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
          setState('unavailable');
          return;
        }
        throw err;
      }

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setState(permission === 'denied' ? 'denied' : 'off');
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
      });
      const json = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
      if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) throw new Error('Incomplete subscription');
      await api.pushSubscribe(slug, {
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      });
      setState('on');
    } catch (err) {
      setState('off');
      setFailure(err instanceof ApiError ? err.message : 'Could not turn notifications on. Try again in a moment.');
    }
  }

  async function disable() {
    setState('working');
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.pushUnsubscribe(slug, sub.endpoint);
        await sub.unsubscribe();
      }
      setState('off');
    } catch {
      setState('on');
      setFailure('Could not turn notifications off here. You can also turn them off in your browser settings.');
    }
  }

  const copy: Record<PushState, { title: string; body: string }> = {
    unsupported: {
      title: 'This browser cannot do notifications',
      body: 'Install Sneaky Panda as an app, or open it in a browser that supports push.',
    },
    'install-first': {
      title: 'Install the app first, then turn these on',
      body: 'On an iPhone, Apple only allows notifications once Sneaky Panda is installed. Tap Share at the bottom of Safari, then Add to Home Screen, open it from your home screen and come back here.',
    },
    unavailable: {
      title: 'Notifications are not switched on for this business yet',
      body: 'Your alerts still appear in this list whenever you open the app.',
    },
    denied: {
      title: 'Notifications are blocked',
      body: 'Your browser is blocking them. Allow notifications for this site in your browser settings, then come back.',
    },
    off: { title: 'Get a ding on your phone', body: 'When the roster goes out and before your shifts start.' },
    on: { title: 'Notifications are on', body: 'You will get a ding on this device.' },
    working: { title: 'One moment', body: 'Setting that up.' },
  };

  const c = copy[state];
  const canToggle = state === 'off' || state === 'on';

  return (
    <>
      <div className="card mb-4 flex items-center gap-3 p-4">
        <span
          className="flex h-10 w-10 shrink-0 items-center justify-center"
          style={{
            borderRadius: 'var(--sp-radius-pill)',
            background: state === 'on' ? 'var(--sp-accent-soft)' : 'var(--sp-surface-2)',
            color: state === 'on' ? 'var(--sp-accent-strong)' : 'var(--sp-text-faint)',
            border: '1px solid var(--sp-border)',
          }}
          aria-hidden="true"
        >
          {state === 'working' ? <Spinner size={17} /> : state === 'on' ? <BellRing size={18} /> : <BellOff size={18} />}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-bold text-ink">{c.title}</div>
          <div className="text-[12.5px] leading-snug text-muted">{c.body}</div>
        </div>
        {canToggle ? (
          <button
            type="button"
            className={state === 'on' ? 'btn btn-sm' : 'btn btn-primary btn-sm'}
            onClick={() => void (state === 'on' ? disable() : enable())}
          >
            {state === 'on' ? 'Turn off' : 'Turn on'}
          </button>
        ) : null}
      </div>

      {/* A quiet phone looks identical whether alerts are broken or nothing has
          happened worth telling you about. This tells the two apart, from the
          phone itself, without anybody reading a server log. */}
      {state === 'on' ? (
        <button
          type="button"
          className="btn mb-4 w-full"
          disabled={test.isPending}
          onClick={() => test.mutate()}
        >
          {test.isPending ? (
            <Spinner size={15} label="Sending" />
          ) : (
            <>
              <Send size={15} aria-hidden="true" />
              Send a test alert to this phone
            </>
          )}
        </button>
      ) : null}
      <OutcomeModal
        open={failure !== null}
        onClose={() => setFailure(null)}
        ok={false}
        title="Notifications"
        message={failure ?? ''}
      />
      <OutcomeModal
        open={tested !== null}
        onClose={() => setTested(null)}
        ok={tested?.ok ?? false}
        title={tested?.ok ? 'Test sent' : 'Not set up yet'}
        message={tested?.message ?? ''}
      />
    </>
  );
}

function DingPrefs({ slug }: { slug: string }) {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: qk.dingPrefs(slug), queryFn: () => api.dingPrefs(slug), enabled: slug !== '' });
  // The staff-safe slice, not `GET /settings`, which is boss-gated and would
  // 403 for everybody this screen is built for.
  const settings = useQuery({
    queryKey: qk.mySettings(slug),
    queryFn: () => api.mySettings(slug),
    enabled: slug !== '',
    retry: false,
  });
  const [failure, setFailure] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (extra: DingRoutineEntry[]) => api.updateDingPrefs(slug, extra),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.dingPrefs(slug) }),
    onError: (err) =>
      setFailure(err instanceof ApiError ? err.message : 'Could not save your ding preferences. Try again in a moment.'),
  });

  const extra = query.data?.extraDings ?? [];
  // The tenant routine can come from either place; the settings call is the
  // fallback when the prefs endpoint does not echo it back.
  const routine = query.data?.tenantRoutine ?? settings.data?.dingRoutine ?? [];

  return (
    <>
      <section className="card p-4">
        <h2 className="text-[14px] font-bold text-ink">Your dings</h2>
        <p className="mt-0.5 text-[12.5px] leading-relaxed text-muted">
          Your workplace sets the ones below. You can add extra reminders for yourself on top of them.
        </p>

        <h3 className="mt-4 text-[11px] font-bold uppercase tracking-wide text-faint">Set by your workplace</h3>
        {routine.length === 0 ? (
          <p className="mt-1 text-[13px] text-muted">Your workplace has not set any dings.</p>
        ) : (
          <ul className="mt-2 space-y-1.5">
            {routine.map((d, i) => (
              <li
                key={`${d.event}-${i}`}
                className="flex items-center gap-2 rounded-card border px-3 py-2 text-[13px]"
                style={{ background: 'var(--sp-surface-2)', borderColor: 'var(--sp-border)' }}
              >
                <Check size={14} aria-hidden="true" style={{ color: 'var(--sp-accent)' }} />
                <span className="flex-1 text-ink">{describe(d)}</span>
                <span className="chip chip-neutral">Always on</span>
              </li>
            ))}
          </ul>
        )}

        <h3 className="mt-5 text-[11px] font-bold uppercase tracking-wide text-faint">Yours</h3>
        {extra.length === 0 ? (
          <p className="mt-1 text-[13px] text-muted">None yet.</p>
        ) : (
          <ul className="mt-2 space-y-1.5">
            {extra.map((d, i) => (
              <li key={`${d.event}-${i}`} className="flex items-center gap-2">
                <select
                  className="field"
                  aria-label="Ding event"
                  value={d.event}
                  onChange={(e) =>
                    save.mutate(extra.map((x, j) => (j === i ? { ...x, event: e.target.value } : x)))
                  }
                >
                  {Object.entries(EVENT_LABEL).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
                <input
                  className="field tabular"
                  style={{ width: 84 }}
                  type="number"
                  aria-label="Hours before or after"
                  value={d.offsetHours}
                  onChange={(e) =>
                    save.mutate(
                      extra.map((x, j) => (j === i ? { ...x, offsetHours: Number(e.target.value) || 0 } : x)),
                    )
                  }
                />
                <button
                  type="button"
                  className="btn btn-icon btn-sm"
                  onClick={() => save.mutate(extra.filter((_, j) => j !== i))}
                  title="Remove this reminder|Only yours. The ones your workplace sets stay."
                  aria-label="Remove this reminder"
                >
                  <Trash2 size={14} aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <button
          type="button"
          className="btn btn-sm mt-3"
          disabled={save.isPending}
          onClick={() => save.mutate([...extra, { event: 'shift_reminder', offsetHours: -2 }])}
        >
          <Plus size={14} aria-hidden="true" />
          Add a reminder
        </button>
      </section>
      <OutcomeModal
        open={failure !== null}
        onClose={() => setFailure(null)}
        ok={false}
        title="Could not save"
        message={failure ?? ''}
      />
    </>
  );
}

export default function NotificationsPage() {
  const { tenant } = useSession();
  const slug = tenant?.tenantSlug ?? '';
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: qk.notifications(slug),
    queryFn: () => api.notifications(slug),
    enabled: slug !== '',
  });

  const markRead = useMutation({
    mutationFn: (ids?: string[]) => api.markNotificationsRead(slug, ids),
    onSettled: () => void qc.invalidateQueries({ queryKey: qk.notifications(slug) }),
  });

  if (query.isLoading) return <LoadingBlock label="Loading your alerts" />;
  if (query.isError) return <ErrorState error={query.error} what="your alerts" onRetry={() => void query.refetch()} />;

  const items = query.data?.items ?? [];
  const unread = items.filter((n) => n.readAt === null);

  return (
    <div className="px-4 py-4">
      <header className="mb-4 flex items-end gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-[20px] font-extrabold tracking-tight text-ink">Alerts</h1>
          <p className="text-[13px] text-muted">
            {unread.length === 0 ? 'All caught up' : `${unread.length} unread`}
          </p>
        </div>
        {unread.length > 0 ? (
          <button type="button" className="btn btn-sm" onClick={() => markRead.mutate(undefined)}>
            <Check size={14} aria-hidden="true" />
            Mark all read
          </button>
        ) : null}
      </header>

      <PushCard slug={slug} />

      {items.length === 0 ? (
        <EmptyState
          icon={<Bell size={20} aria-hidden="true" />}
          title="Nothing to tell you yet"
          body="Roster updates and shift reminders land here."
        />
      ) : (
        <div className="card mb-5 overflow-hidden">
          {items.map((n, i) => (
            <button
              key={n.id}
              type="button"
              onClick={() => n.readAt === null && markRead.mutate([n.id])}
              className="press flex w-full items-start gap-3 px-4 py-3 text-left"
              style={{
                borderTop: i > 0 ? '1px solid var(--sp-hairline)' : undefined,
                background: n.readAt === null ? 'var(--sp-accent-soft)' : undefined,
                transition: 'background 300ms var(--sp-ease-out)',
              }}
            >
              <span
                className="mt-1 h-2 w-2 shrink-0"
                aria-hidden="true"
                style={{
                  borderRadius: 'var(--sp-radius-pill)',
                  background: n.readAt === null ? 'var(--sp-accent)' : 'var(--sp-border-strong)',
                }}
              />
              <span className="min-w-0 flex-1">
                <span className="block text-[13.5px] font-bold text-ink">
                  {n.payload.title ?? EVENT_LABEL[n.type] ?? n.type.replace(/_/g, ' ')}
                </span>
                {n.payload.body ? (
                  <span className="block text-[13px] leading-snug text-muted">{n.payload.body}</span>
                ) : null}
                <span className="mt-0.5 block text-[11.5px] text-faint">{fmtDateTime12(n.createdAt)}</span>
              </span>
            </button>
          ))}
        </div>
      )}

      <DingPrefs slug={slug} />
    </div>
  );
}
